import { qwanyxCollection } from 'qwanyx';

/**
 * Checks if an item is an ancestor of another item in the tree
 * This prevents creating circular references when moving items
 * 
 * @param {string} potentialAncestorId - ID of the item that might be an ancestor
 * @param {string} descendantId - ID of the item we're checking against
 * @param {number} maxDepth - Maximum depth to check to prevent infinite loops
 * @returns {Promise<boolean>} - Returns true if potentialAncestorId is an ancestor of descendantId
 */
export async function isAncestor(potentialAncestorId, descendantId, maxDepth = 100) {
    // Base case: If ids are the same, return false (not an ancestor of itself)
    if (potentialAncestorId === descendantId) {
        return false;
    }
    
    try {
        const collection = qwanyxCollection();
        
        // Start with the descendant ID
        let currentId = descendantId;
        let depth = 0;
        
        // Keep track of visited IDs to detect any existing circular references
        const visitedIds = new Set();
        
        // Traverse up the tree until we reach the root or find the potential ancestor
        while (currentId && depth < maxDepth) {
            // Check if we've already visited this ID (detects existing circular references)
            if (visitedIds.has(currentId)) {
                console.warn(`Circular reference detected in tree at ID: ${currentId}`);
                break;
            }
            
            // Add current ID to visited set
            visitedIds.add(currentId);
            
            // Get the current item to find its parent (ed)
            // Using findOne which is supported in MongoDB Realm
            const currentItem = await collection.findOne({ _id: currentId });
            
            // If no item found or no parent, we've reached the end
            if (!currentItem || !currentItem.ed) {
                break;
            }
            
            // If the parent is the potential ancestor, return true
            if (currentItem.ed === potentialAncestorId) {
                return true;
            }
            
            // Move up to the parent for the next iteration
            currentId = currentItem.ed;
            depth++;
        }
        
        // If we've checked all ancestors and didn't find the potential ancestor, return false
        return false;
    } catch (error) {
        console.error("Error checking ancestry:", error);
        // In case of error, return true to be safe (preventing potentially harmful operations)
        return true;
    }
}

/**
 * Check if a drop operation would create a circular reference
 * 
 * @param {Object} draggedItem - The item being dragged
 * @param {string} targetId - The ID of the drop target
 * @returns {Promise<boolean>} - Returns true if the drop is valid, false if it would create a circular reference
 */
export async function isValidDrop(draggedItem, targetId) {
    // If item is dropped on itself, it's invalid
    if (draggedItem.itemId === targetId) {
        return false;
    }
    
    // If the dragged item is an ancestor of the target, the drop would create a circular reference
    const ancestryCheck = await isAncestor(draggedItem.itemId, targetId);
    
    // Return the inverse result (true if it's not an ancestor, false if it is)
    return !ancestryCheck;
}

export default isValidDrop;